/**
 * Tells whether a checklist is defined for an element type. The comparison ignores case and
 * surrounding spaces, like the backend endpoint that returns the types.
 *
 * @param elementType type ("tipologia") of the water supply scheme element
 * @param checklistElementTypes element types with a checklist, already normalized by the backend
 */
/**
 * Tells whether a numeric checklist value is allowed (same rules as the backend): never negative,
 * whole numbers for counts ("Numero ..."), at most 100 for percentages ("%"). Empty values are allowed.
 *
 * @param name field name of a "number" item, or sub-field name ("%", "km") of a "dettagli" item
 * @param value value entered by the user
 */
export function isChecklistNumberAllowed(name: string | null | undefined, value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return false;
  const normalizedName = (name ?? '').trim().toLowerCase();
  if (normalizedName === '%') return number <= 100;
  return !normalizedName.startsWith('numero') || Number.isInteger(number);
}

export function isChecklistDateField(name: string | null | undefined): boolean {
  return (name ?? '').trim().toLowerCase() === 'data sopralluogo';
}

export function isChecklistCoordinatesField(name: string | null | undefined): boolean {
  return (name ?? '').trim().toLowerCase().startsWith('coordinate gps');
}

// same ranges as the backend (checklist-validation.ts): Lat/Lon within Italy in either order, or E/N in
// metres covering UTM and Gauss-Boaga
const LAT_ITALIA = { min: 35, max: 48 };
const LON_ITALIA = { min: 6, max: 19 };
const EST_METRI = { min: 100000, max: 2900000 };
const NORD_METRI = { min: 3800000, max: 5400000 };

function inRange(value: number, range: { min: number; max: number }): boolean {
  return value >= range.min && value <= range.max;
}

function areCoordinates(text: string): boolean {
  const numbers = (text.match(/-?\d+(?:[.,]\d+)?/g) ?? []).map((n) => Number(n.replace(',', '.')));
  if (numbers.length !== 2) return false;
  const [first, second] = numbers;
  const latLon = (inRange(first, LAT_ITALIA) && inRange(second, LON_ITALIA)) || (inRange(first, LON_ITALIA) && inRange(second, LAT_ITALIA));
  return latLon || (inRange(first, EST_METRI) && inRange(second, NORD_METRI));
}

/**
 * Format rule of free text items (same rules as the backend): dates as dd/mm/yyyy, existing and not after
 * today; coordinates as exactly two numbers forming a Lat/Lon pair in Italy or an E/N pair in metres,
 * labels and separators tolerated. Other text and empty values are allowed.
 */
export function isChecklistTextAllowed(name: string | null | undefined, value: unknown, today: Date = new Date()): boolean {
  if (value === null || value === undefined || String(value).trim() === '') return true;
  const text = String(value).trim();
  if (isChecklistDateField(name)) {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
    if (!match) return false;
    const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(year, month - 1, day);
    const exists = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return exists && date <= new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
  if (isChecklistCoordinatesField(name)) return areCoordinates(text);
  return true;
}

/** dd/mm/yyyy text of a date picked from the calendar (month from 1 to 12) */
export function formatChecklistDate(day: number, month: number, year: number): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(day)}/${pad(month)}/${year}`;
}

export function isChecklistElementType(elementType: string | null | undefined, checklistElementTypes: string[] | null | undefined): boolean {
  if (!elementType || !checklistElementTypes?.length) return false;
  return checklistElementTypes.includes(elementType.trim().toLowerCase());
}
